





































import { computed, defineComponent, onMounted, ref, ssrRef, useFetch, useContext } from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { usePageResolver } from '~/stores/page';
import { extendProductsForBox } from '~/utils/solepro/products';
import Spinner from '~/components/solepro/Spinner.vue';

import { ProductBox } from '~/utils/types/Product';
import CategoryInfos from '~/components/solepro/Category/CategoryInfos.vue';
import CategoryProductList from '~/components/solepro/Category/CategoryProductList.vue';

import {
  useCategory,
  useFacet,
  useUiHelpers,
  useUiState,
} from '~/composables';

import facetGetters from '~/modules/catalog/category/getters/facetGetters';

import { usePrice } from '~/modules/catalog/pricing/usePrice';
import categoryContentQuery from '~/modules/catalog/category/components/cms/categoryContent.gql';

//import { useCategoryContent } from '~/modules/catalog/category/components/cms/useCategoryContent';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import { useCategoryStore } from '~/modules/catalog/category/stores/category';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { categoryView, categoryProductCardTrack, categoryNavigationTrack } from '~/helpers/trackings/24orepro/categories';
import { getProductFilterByCategoryCommand } from '~/modules/catalog/category/components/filters/command/getProductFilterByCategoryCommand';
import { useApi } from '~/composables/useApi';
import { createProductAttributeFilterInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput';
import { isFilterEnabled, isFilterEnabledInCategory } from '~/modules/catalog/category/config/FiltersConfig';
import HTMLContent from '~/components/solepro/HTMLContent.vue';

import type { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import type { Pagination } from '~/composables/types';
import type { Aggregation } from '~/modules/GraphQL/types';
import { LOGO } from '~/utils/constants/layout';


export default defineComponent({
  name: 'CategoryPage',
  components: {
    CategoryInfos,
    HTMLContent,
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryProductList,
    Spinner,
  },
  transition: 'fade',
  setup() {
    const { resolveUrl } = usePageResolver();
    //const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { addTags } = useCache();
    const { query } = useApi();
    const uiHelpers = useUiHelpers();
    const { app, $config } = useContext();
    const products = ssrRef<ProductBox[]>([]);
    const allCategories = ref([]);
    const categoryUid = ref(null);
    const filters = ref<Aggregation[]>([]);
    const totalResults = ref(0);
    const sortBy = ref<SortingModel>({ selected: '', options: [] });
    const pagination = ref<Pagination>({});

    const productContainerElement = ref<HTMLElement | null>(null);

    const {
      toggleFilterSidebar,
    } = useUiState();
    const { result, search } = useFacet(app.$config.testingCategories);

    const categoryMeta = ref(null);

    const { activeCategory, loadCategoryTree } = useTraverseCategory();
    const activeCategoryName = computed(() => activeCategory.value?.name ?? '');

    const relatedFilterCode = computed(()=> activeCategory.value?.related_filter_code ?? '');
    const firstEditorialBox = computed(()=>activeCategory.value?.editorial_box_1 ?? '')
    const secondEditorialBox = computed(()=>activeCategory.value?.editorial_box_2 ?? '')
    const firstEditorialBoxPosition  = computed(()=>activeCategory.value?.editorial_box_1_position ?? 4)
    const secondEditorialBoxPosition = computed(()=>activeCategory.value?.editorial_box_2_position ?? 10)
    const footerDescription = computed(()=>activeCategory.value?.description_footer ?? '')

    const activeCategoryDescription = computed(() => activeCategory.value?.description ?? '');

    const trackPageView = async (categoryName) => {
      categoryView(categoryName);
    };



    const { fetch } = useFetch(async () => {
      const routeData = await resolveUrl();
      categoryUid.value = routeData.uid;

      if (!activeCategory.value) {
        await loadCategoryTree();
      }

      const categoryStore = useCategoryStore();
      const filterQueryParams = createProductAttributeFilterInput({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid.value })

      allCategories.value = categoryStore.rawCategories?.items[0].children;

      const [ categoryMetaData, fetchedFilters] = await Promise.all([
        //getContentData(categoryUid.value as string),
        loadCategoryMeta({ category_uid: categoryUid.value }),
        getProductFilterByCategoryCommand.execute(filterQueryParams, query),
        search({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid.value }),
      ]);

      categoryMeta.value = categoryMetaData;
      
      filters.value = getCurrentFilters(fetchedFilters);

      products.value = extendProductsForBox(allCategories.value, facetGetters.getProducts(result.value)) ?? [];
      sortBy.value = facetGetters.getSortOptions(result.value);
      totalResults.value = result.value.data.total;
      pagination.value = facetGetters.getPagination(result.value);

      const tags = [{ prefix: CacheTagPrefix.View, value: categoryUid.value }];
      const productTags = products.value.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product.uid,
      }));

      addTags([...tags, ...productTags]);
      (app.$vsf.$magento.config.state as any).setCategory(categoryUid.value);
    });
    
    const isPriceLoaded = ref(false);
    
    onMounted(async () => {
      trackPageView(activeCategoryName.value);
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)?.price_range;

          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }

          return { ...product };
        });
      }

      isPriceLoaded.value = true;
    });

    const goToPage = (page: number) => {
      
      if (page != 0) {
        categoryNavigationTrack(activeCategoryName.value, page)
      }

      uiHelpers.changePage(page, false);
      fetch();
      productContainerElement.value.scrollIntoView();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(0);
    };

    const getCurrentFilters = (fetchedFilters) => {
      return fetchedFilters
        .filter((filter) => isFilterEnabled(filter.attribute_code))
        .filter((filter) => isFilterEnabledInCategory(filter.enabled_categories, categoryUid.value))
        .filter((filter) => filter.attribute_code != relatedFilterCode.value)
        .sort((a, b) => {
          const getPosition = (item) => item.position !== null ? item.position : Infinity;
            return getPosition(a) - getPosition(b);
      });
    }

    const onReloadProducts = () => {
      goToPage(0);
    };

    const trackCategoryProductCard = (productName) => {
      const categoryName = activeCategoryName.value ? activeCategoryName.value : "search";
      categoryProductCardTrack(categoryName, productName)
    }

    return {
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      pagination,
      products,
      sortBy,
      filters,
      relatedFilterCode,
      firstEditorialBox,
      secondEditorialBox,
      firstEditorialBoxPosition,
      secondEditorialBoxPosition,
      footerDescription,
      activeCategoryName,
      activeCategoryDescription,
      totalResults,
      categoryUid,
      trackCategoryProductCard,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      config: $config
    };
  },
  head() {
    const ProductListItems = (this.products as any).map((product, index) => {
      return {
        "@type": "ListItem",
        "position": index + 1,
        "url": `${this.config.soleproDomain}/24orepro/${product.url_key}${product.url_suffix}`
      }
    })
    return {
      ...getMetaInfo({...(this.categoryMeta as any), title: this.activeCategoryName}, false, 'category'),
      link:[{
          rel: 'preload',
          as: 'image',
          href: LOGO,
          fetchpriority: 'high'
      }],
      script: [
        {
          hid: 'ld-product-list',
          type: 'application/ld+json',
          json: {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": ProductListItems
          }
        }
      ]
    }
  }
});
